import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { SharedModule } from '../shared/shared.module';
import { addIcons } from 'ionicons';
import {
  menuOutline,
  close,
  chevronDownOutline,
  powerOutline,
  saveOutline,
  homeOutline,
  timeOutline,
  refreshOutline,
} from 'ionicons/icons';
import { TranslationService, LanguageItem } from '../shared/services/translation.service';
import { parseQueryParams } from '../shared/utils/parse-query-params';
import { filter, lastValueFrom } from 'rxjs';
import { ApplicationService, AuthService } from '../shared/services';
import { SimulationWizardService } from '../pages/simulation/simulation-wizard.service';
import { ImpersonationBannerComponent } from '../components/impersonation-banner/impersonation-banner.component';
import { WizardBrandingService } from '../pages/wizards/wizard-branding.service';
import { SolarRentWizardStateService } from '../pages/wizards/solar-rent-wizard/shared/solar-rent-wizard.state';
import { SOLAR_RENT_SINGLE_SIGNATURE_FOR_ALL } from '../pages/wizards/solar-rent-wizard/shared/solar-rent-wizard.config';

@Component({
  selector: 'app-header',
  standalone: true,
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [SharedModule, ImpersonationBannerComponent],
})
export class HeaderComponent implements OnInit, AfterViewInit {
  appName = '';
  isMenuOpen = false;
  isSimulationPage = false;
  languages: LanguageItem[] = [];
  languageSelectPopoverOptions = { cssClass: 'language-select-popover' };
  readonly activeWizardBranding = this.wizardBrandingService.activeBranding;
  private readonly headerTranslationTick = signal(0);
  private readonly currentUrl = signal('');
  readonly headerSubtitleKey = computed(() => {
    const branding = this.activeWizardBranding();
    switch (branding.key) {
      case 'solar-rent':
        return 'shared.application.wizard.solar_rent.header.title';
      default:
        return '';
    }
  });
  readonly headerSubtitleText = computed(() => {
    this.headerTranslationTick();

    const branding = this.activeWizardBranding();
    const translationKey = this.headerSubtitleKey();

    if (!translationKey) {
      return branding.displayName;
    }

    const translated = this.translation.translate.instant(translationKey);

    return translated && translated !== translationKey
      ? translated
      : branding.displayName;
  });
  /** Applications shortcut (clock) button: only for managers. */
  readonly showManagerStatusButton = computed(() => this.authService.isManager());

  readonly isManager = computed(() => this.authService.isManager());

  /** Save button visible when either application or simulation wizard is active and editable. */
  readonly showSaveButton = computed(
    () =>
      this.showSolarRentSaveButton() || this.simulationWizardService.showSaveInHeader()
  );

  readonly showSolarRentSaveButton = computed(
    () => this.isSolarRentWizardRoute() && this.extractSolarRentContractIdFromUrl(this.currentUrl()) !== ''
  );

  /** Red dot when either wizard has unsaved changes. */
  showUnsavedIndicator(): boolean {
    return (
      (
        this.showSolarRentSaveButton()
        && !this.solarRentWizardState.isSavingPurposeStep
        && this.solarRentWizardState.hasUnsavedPurposeStepChanges
      ) ||
      this.simulationWizardService.showUnsavedIndicator()
    );
  }

  /** Tooltip and aria-label for the save button (application vs simulation). */
  readonly saveButtonTooltip = computed(() => {
    if (this.simulationWizardService.showSaveInHeader()) {
      return this.translation.translate.instant('application.simulation.save') || 'Save simulation';
    }
    return this.translation.translate.instant('application.wizard.application.save') || 'Save';
  });

  private readonly defaultTopBarHeightPx = 56;

  constructor(
    private destroyRef: DestroyRef,
    private elRef: ElementRef<HTMLElement>,
    private router: Router,
    private location: Location,
    private applicationService: ApplicationService,
    private authService: AuthService,
    public translation: TranslationService,
    private simulationWizardService: SimulationWizardService,
    private wizardBrandingService: WizardBrandingService,
    private solarRentWizardState: SolarRentWizardStateService,
  ) {
    addIcons({ menuOutline, close, chevronDownOutline, powerOutline, saveOutline, homeOutline, timeOutline, refreshOutline });
  }

  get currentLanguage(): string {
    return this.translation.translate.currentLang
      || this.translation.translate.getCurrentLang()
      || localStorage.getItem('language')
      || 'en';
  }

  onSaveApplication(): void {
    if (this.showSolarRentSaveButton()) {
      this.saveSolarRentContract();
    } else if (this.simulationWizardService.showSaveInHeader()) {
      this.simulationWizardService.requestSave();
    }
  }

  get currentLang(): string {
    const lang = this.translation.translate.currentLang ?? this.translation.translate.getCurrentLang();
    return (lang || 'en').toUpperCase().slice(0, 2);
  }

  onLanguageChange(lang: string): void {
    this.translation.setTranslationTo(lang);
  }

  async logout(event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();
    await this.authService.logout();
  }
  
  getFlagEmoji(countryCode: string): string {
    if (!countryCode || countryCode.length !== 2) {
      return '🌐';
    }
    return String.fromCodePoint(
      ...countryCode
        .toUpperCase()
        .split('')
        .map((char) => 127397 + char.charCodeAt(0))
    );
  }

  async ngOnInit(): Promise<void> {
    this.syncHeaderContext(this.router.url);
    this.exposeResetSignatureGlobal();

    const languageList = await lastValueFrom(this.translation.getLanguages(parseQueryParams(
      {
        pageIndex: 1,
        pageSize: 100,
        sortField: 'createdAt',
        sortOrder: 'ascend',
        where: { active: true },
        searchOption: 'EXACT',
      },
      'ASC',
    )));
    this.languages = languageList;
    const codes = languageList.map((l) => l.languageISO2);
    if (codes.length) {
      this.translation.translate.addLangs(codes);
    }
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.syncHeaderContext(event.urlAfterRedirects);
      });

    this.translation.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.headerTranslationTick.update((tick) => tick + 1);
      });
  }

  private syncHeaderContext(url: string): void {
    this.currentUrl.set(url);
    this.isMenuOpen = url.includes('/menu');
    this.isSimulationPage = url.split('?')[0].split('#')[0].includes('/simulation');
    // this.isSimulationPage = url.split('?')[0].split('#')[0].includes('/simulation') && (url.includes('?or=r') || url.includes('&or=r'));
    if (this.shouldPreserveApplicationContext(url)) {
      return;
    }
    this.wizardBrandingService.setActiveBrandingFromUrl(url);
    this.loadApplicationNameForUrl(url);
  }

  private shouldPreserveApplicationContext(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/menu';
  }

  private loadApplicationNameForUrl(url: string): void {
    const applicationId = this.extractApplicationIdFromUrl(url);
    if (!applicationId) {
      this.appName = '';
      return;
    }

    this.applicationService.setApplicationId(applicationId);
    this.applicationService.getApplicationById(applicationId).subscribe({
      next: (response: any) => {
        const resolvedApplicationScheme =
          response?.data?.application_type ??
          response?.application_type ??
          response?.data?.applicationType ??
          response?.applicationType ??
          null;
        const resolvedName =
          response?.data?.name ??
          response?.name ??
          response?.data?.application_name ??
          response?.application_name ??
          '';
        this.wizardBrandingService.setActiveBrandingFromApplicationScheme(resolvedApplicationScheme);
        if (typeof resolvedName === 'string' && resolvedName.trim()) {
          this.appName = resolvedName.trim();
        }
      },
      error: () => {
        this.appName = '';
      },
    });
  }

  private extractApplicationIdFromUrl(url: string): number | null {
    const path = url.split('?')[0];
    const segments = path.split('/').filter(Boolean);

    let rawApplicationId: string | undefined;

    if (segments[0] === 'application' || segments[0] === 'simulation') {
      rawApplicationId = segments[1];
    } else if (segments[1] === 'application') {
      rawApplicationId = segments[2];
    }

    if (!rawApplicationId) {
      return null;
    }

    const applicationId = Number(rawApplicationId);
    return Number.isInteger(applicationId) && applicationId > 0 ? applicationId : null;
  }

  ngAfterViewInit(): void {
    this.updateTopBarHeightCssVar();
    const el = this.elRef.nativeElement;
    if (el && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => this.updateTopBarHeightCssVar());
      ro.observe(el);
      this.destroyRef.onDestroy(() => ro.disconnect());
    }
  }

  private updateTopBarHeightCssVar(): void {
    const el = this.elRef.nativeElement;
    const height =
      el?.offsetHeight ?? el?.getBoundingClientRect?.()?.height ?? this.defaultTopBarHeightPx;
    const px = `${Math.round(height)}px`;
    document.documentElement.style.setProperty('--app-top-bar-height', px);
  }

  private isSolarRentWizardRoute(): boolean {
    const path = this.currentUrl().split('?')[0].split('#')[0];
    return path.includes('/solar-rent');
  }

  private extractSolarRentContractIdFromUrl(url: string): string {
    const pathSegments = url.split('?')[0].split('#')[0].split('/').filter(Boolean);
    const solarRentIndex = pathSegments.indexOf('solar-rent');

    if (solarRentIndex === -1) {
      return '';
    }

    return String(pathSegments[solarRentIndex + 1] ?? '').trim();
  }

  private saveSolarRentContract(): void {
    if (this.solarRentWizardState.isSavingPurposeStep) {
      return;
    }

    const contractId = this.extractSolarRentContractIdFromUrl(this.currentUrl()) || this.solarRentWizardState.loadedContractId || '';
    const baseUrl = this.solarRentWizardState.apiBaseUrl;
    const token = this.authService.getToken();

    if (!contractId || !baseUrl) {
      return;
    }

    this.solarRentWizardState.savePurposeStep(contractId, baseUrl, token).subscribe({
      error: (error) => {
        console.error('[HeaderComponent] Failed to save Solar Rent contract', error);
      },
    });
  }

  /**
   * Resets the contract signatures and persists the change.
   *
   * Exposed on `window.resetSignature()` for manual use from the browser console.
   * Returns a short report so the console shows what actually happened, instead of
   * silently doing nothing when the contract is finalized or nothing was signed.
   */
  resetSignature(): { reset: boolean; reason?: string; saved: boolean } {
    if (this.solarRentWizardState.isContractFinalized) {
      console.warn('[resetSignature] Contract is finalized, nothing reset.');
      return { reset: false, reason: 'contract_finalized', saved: false };
    }

    if (SOLAR_RENT_SINGLE_SIGNATURE_FOR_ALL) {
      this.solarRentWizardState.resetSignatureDocuments();
    } else {
      const currentDocumentKey = this.solarRentCurrentSignatureDocumentKey();
      if (!currentDocumentKey) {
        console.warn('[resetSignature] No signature document could be resolved from the URL.');
        return { reset: false, reason: 'no_current_document', saved: false };
      }
      this.solarRentWizardState.resetSignatureDocuments(currentDocumentKey);
    }

    // A fresh signing round needs a fresh SMS OTP.
    this.solarRentWizardState.clearSigningOtpToken();

    this.saveSolarRentContract();

    console.info('[resetSignature] Signatures reset and save requested.');
    return { reset: true, saved: true };
  }

  private exposeResetSignatureGlobal(): void {
    (window as unknown as Record<string, unknown>)['resetSignature'] = () => this.resetSignature();
  }

  private solarRentCurrentSignatureDocumentKey(): string {
    const pathSegments = this.currentUrl().split('?')[0].split('#')[0].split('/').filter(Boolean);
    const solarRentIndex = pathSegments.indexOf('solar-rent');
    if (solarRentIndex === -1) {
      return '';
    }

    const stepSlug = String(pathSegments[solarRentIndex + 2] ?? '').trim().toLowerCase();
    if (!stepSlug) {
      return '';
    }

    if (stepSlug === 'signatures') {
      const finalDocument = this.solarRentWizardState.signatureDocumentDefinitions
        .find((definition) => definition.type === 'final-signature');
      return finalDocument?.key ?? '';
    }

    const matchedDocument = this.solarRentWizardState.signatureDocumentDefinitions
      .find((definition) => (
        definition.subSelected.toLowerCase() === stepSlug
        || definition.key.toLowerCase() === stepSlug
      ));

    return matchedDocument?.key ?? '';
  }

  openDashboardWeb(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const dashboardUrl = this.authService.getDashboardAutoLoginUrl('/dashboard');

    if (!dashboardUrl) {
      this.router.navigate(['/login']);
      return;
    }

    window.open(dashboardUrl, '_blank', 'noopener,noreferrer');
  }

  openManagerApplications(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const reference = (this.appName || '').trim();
    const redirectPath = reference
      ? `/applications?lead-status=${encodeURIComponent(reference)}`
      : '/applications';
    const url = this.authService.getDashboardAutoLoginUrl(redirectPath, false);

    if (!url) {
      this.router.navigate(['/login']);
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
